import { type Provider, Injector, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { createApiClient } from '@lance-x/api-client';
import { AuthService } from '../auth/auth.service';
import { AuthStore } from '../auth/auth.store';
import { LoadingService } from '../loading/loading.service';
import { TOKEN_STORE } from '../auth/token-store';
import { WebTokenStore } from '../auth/web-token-store';
import { CapacitorTokenStore } from '../auth/capacitor-token-store';
import { environment } from '../../../environments/environment';
import { API_CLIENT } from './api-client-token';

/**
 * ApiClient provider. Constructed once per app.
 *
 * `getAccessToken` reads from the AuthStore signal so every fetch picks up the
 * freshest token without manual wiring.
 *
 * `getPlatformHeader` lets the backend distinguish PWA cookies vs Capacitor
 * body-mode refresh tokens (auth.controller.ts gates on X-Client-Platform).
 *
 * `onRequestStart` / `onRequestEnd` notify `LoadingService` to manage global visual progress.
 *
 * `onUnauthorized` resolves AuthService lazily (via Injector) to avoid the
 * circular dep AuthService → API_CLIENT. On 401: refresh once; return true so
 * the api-client retries the original request. A second 401 propagates and the
 * caller (or interceptor-less code) routes the user to /welcome.
 */
export const apiClientProvider: Provider = {
  provide: API_CLIENT,
  useFactory: () => {
    const authStore = inject(AuthStore);
    const injector = inject(Injector);
    const loadingService = inject(LoadingService);
    return createApiClient({
      baseUrl: environment.apiBaseUrl,
      getAccessToken: () => authStore.accessToken(),
      getPlatformHeader: () => (Capacitor.isNativePlatform() ? 'capacitor' : 'web'),
      onRequestStart: () => loadingService.startRequest(),
      onRequestEnd: () => loadingService.endRequest(),
      onUnauthorized: async () => {
        // Top-level import + lazy DI lookup breaks the AuthService ↔ API_CLIENT cycle
        // without emitting runtime dynamic chunk requests that fail MIME checking.
        const auth = injector.get(AuthService);
        return await auth.refresh();
      },
    });
  },
};

/**
 * Platform-aware TokenStore provider. Factory runs at first injection;
 * Capacitor.isNativePlatform() is true only on iOS/Android shells.
 */
export const tokenStoreProvider: Provider = {
  provide: TOKEN_STORE,
  useFactory: () => (Capacitor.isNativePlatform() ? new CapacitorTokenStore() : new WebTokenStore()),
};
